import React, { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, Sparkles, Users, FileText, Loader2, Target, ChevronRight, Eye, Play, Wand2, TrendingUp, Plus, Trash2, Edit2, Check, Link2, Clock, ShoppingBag, Zap, RefreshCw, Copy, CheckCircle, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { useToast } from '@/components/ui/use-toast';
import { AEOQuestionsPanel } from '@/components/aeo';
import { AEOReadinessButton, SectorReadinessButton } from '@/components/context-readiness';
import { useLanguage, ContentType } from '@/contexts/LanguageContext';
import { EnrichmentBatchProgress } from '@/components/product-enrichment/EnrichmentBatchProgress';
import { useAuth } from '@/context/auth-context';
import { computeBlockedOutcome } from '@/utils/partialBatchOutcome';

interface CategoryLeafDetailProps {
  category: {
    id: string | null; // null for site-level categories without sector templates
    category_name: string;
    category_path: string;
    full_path?: string; // Full path including subcategory for site-level categories
    hasChildren?: boolean;
  };
  sectorId: number;
  siteId: string; // Added for site-level research
  isOpen: boolean;
  onClose: () => void;
  onNavigateToTab: (tab: string, data?: any) => void;
  onTriggerResearch?: (categoryName: string) => void;
  onCategoriesCreated?: () => void;
  context?: 'sector' | 'offering'; // Context determines what buttons to show
}

interface PersonaRanking {
  persona_name: string;
  persona_description: string;
  image_url: string;
  age_range: string;
  occupation: string;
  benefits_count: number;
  avg_priority_weight: number;
  highest_priority_rank: number;
  benefits: Array<{
    benefit_name: string;
    benefit_description: string;
    priority_rank: number;
    priority_weight: number;
    notes: string;
  }>;
}

interface AttributeMapping {
  id: string;
  technical_attribute: string;
  technical_value_range: string | null;
  perceived_attribute_name: string;
  benefit_layer: string;
  impact_level: 'high' | 'medium' | 'low';
  impact_description: string;
  confidence_score: number;
}

interface PerceptionAttribute {
  attribute_name: string;
  customer_description: string;
  benefit_layer: 'functional' | 'emotional' | 'aspirational';
  purchase_driver_strength: number;
  common_phrases?: string[];
}

interface ExpansionAnalysis {
  categoryName: string;
  totalScore: number;
  scores: {
    searchVolume: number;
    ecommerceValidation: number;
    productDiversity: number;
    semanticAnalysis: number;
    userBehavior: number;
  };
  recommendation: {
    shouldExpand: boolean;
    priority: 'high' | 'medium' | 'low' | 'none';
    confidence: number;
    reasoning: string;
  };
  suggestedSubcategories: Array<{
    name: string;
    description: string;
    marketShare: number;
    confidence: number;
    keyAttributes: string[];
  }>;
  analysis: {
    confidence: number;
    reasoning: string;
    evidence: string[];
  };
}

// Definition Preview Component with expand/collapse
function DefinitionPreview({
  definition,
  categoryName,
  isSiteLevel
}: {
  definition: string;
  categoryName: string;
  isSiteLevel: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  // Truncate to ~2 lines (approximately 150 characters)
  const isLong = definition.length > 150;
  const displayText = expanded || !isLong
    ? definition
    : definition.substring(0, 150).trim() + '...';

  return (
    <div className="mb-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Category Definition
        </h4>
        {isLong && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 h-6 px-2"
          >
            {expanded ? 'Show Less' : 'Read More'}
            <ChevronRight className={`h-3 w-3 ml-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </Button>
        )}
      </div>
      <div className={expanded ? 'max-h-64 overflow-y-auto' : ''}>
        <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed whitespace-pre-wrap">
          {displayText}
        </p>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        {isSiteLevel
          ? `This definition is specific to ${categoryName} products on this site.`
          : `Sector-level definition for ${categoryName} category.`}
      </p>
    </div>
  );
}

export function CategoryLeafDetail({
  category,
  sectorId,
  siteId,
  isOpen,
  onClose,
  onNavigateToTab,
  onTriggerResearch,
  onCategoriesCreated,
  context = 'offering', // Default to offering for backward compatibility
}: CategoryLeafDetailProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  // Role-based research control: Only Super Admins can re-trigger research when data exists
  const isSuperAdmin = user?.role === 'super_admin';

  // Helper: Determine if this is a site-level category (no sector template)
  const isSiteLevelCategory = !category.id;

  // Helper: Extract subcategory name from category path (for site-level categories like "Parent::Cojinetes")
  const getSubcategoryInfo = () => {
    if (category.full_path && category.full_path.includes('::')) {
      const parts = category.full_path.split('::');
      return {
        categoryPath: parts[0],
        subcategoryName: parts[1]
      };
    }
    return {
      categoryPath: category.category_path,
      subcategoryName: null
    };
  };
  const [personaRankings, setPersonaRankings] = useState<PersonaRanking[]>([]);
  const [perceptions, setPerceptions] = useState<PerceptionAttribute[]>([]);
  const [attributeMappings, setAttributeMappings] = useState<AttributeMapping[]>([]);
  const [attributeMappingsError, setAttributeMappingsError] = useState<string | null>(null);
  const [hasResearch, setHasResearch] = useState(false);
  const [hasDefinitionResearch, setHasDefinitionResearch] = useState(false);
  const [categoryDefinition, setCategoryDefinition] = useState<string>('');
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [loadingDefinition, setLoadingDefinition] = useState(false);
  const [loadingBenefits, setLoadingBenefits] = useState(false);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [loadingPersonas, setLoadingPersonas] = useState(false);

  // Multi-language support
  const { language, translate, translateBatch } = useLanguage();
  const [translatedDefinition, setTranslatedDefinition] = useState<string>('');
  const [translatedPerceptions, setTranslatedPerceptions] = useState<PerceptionAttribute[]>([]);
  const [translatedMappings, setTranslatedMappings] = useState<AttributeMapping[]>([]);
  const [translatedPersonaRankings, setTranslatedPersonaRankings] = useState<PersonaRanking[]>([]);
  const [isTranslating, setIsTranslating] = useState(false);

  // Translate content when language or data changes
  const translateContent = useCallback(async () => {
    // Skip translation if English (source language)
    if (language === 'en') {
      setTranslatedDefinition(categoryDefinition);
      setTranslatedPerceptions(perceptions);
      setTranslatedMappings(attributeMappings);
      setTranslatedPersonaRankings(personaRankings);
      return;
    }

    setIsTranslating(true);
    try {
      // Translate category definition
      if (categoryDefinition) {
        const defResult = await translate(categoryDefinition, 'category_description', `${category.category_name}_definition`);
        setTranslatedDefinition(defResult.translated);
      }

      // Translate perceptions (perceived benefits)
      if (perceptions.length > 0) {
        const translatedPerceptionsList = await Promise.all(
          perceptions.map(async (p, idx) => {
            const [nameResult, descResult] = await Promise.all([
              translate(p.attribute_name, 'perceived_benefit', `${category.category_name}_benefit_${idx}_name`),
              translate(p.customer_description, 'perceived_benefit', `${category.category_name}_benefit_${idx}_desc`)
            ]);
            return {
              ...p,
              attribute_name: nameResult.translated,
              customer_description: descResult.translated
            };
          })
        );
        setTranslatedPerceptions(translatedPerceptionsList);
      }

      // Translate attribute mappings
      if (attributeMappings.length > 0) {
        const translatedMappingsList = await Promise.all(
          attributeMappings.map(async (m, idx) => {
            const [nameResult, descResult] = await Promise.all([
              translate(m.perceived_attribute_name, 'attribute_mapping', `${category.category_name}_mapping_${idx}_name`),
              translate(m.impact_description, 'attribute_mapping', `${category.category_name}_mapping_${idx}_desc`)
            ]);
            return {
              ...m,
              perceived_attribute_name: nameResult.translated,
              impact_description: descResult.translated
            };
          })
        );
        setTranslatedMappings(translatedMappingsList);
      }

      // Translate persona rankings descriptions
      if (personaRankings.length > 0) {
        const translatedPersonasList = await Promise.all(
          personaRankings.map(async (p, idx) => {
            if (p.persona_description) {
              const descResult = await translate(p.persona_description, 'buyer_persona', `${category.category_name}_persona_${idx}_desc`);
              return {
                ...p,
                persona_description: descResult.translated
              };
            }
            return p;
          })
        );
        setTranslatedPersonaRankings(translatedPersonasList);
      }
    } catch (error) {
      console.error('Translation error:', error);
      // Fallback to original content on error
      setTranslatedDefinition(categoryDefinition);
      setTranslatedPerceptions(perceptions);
      setTranslatedMappings(attributeMappings);
      setTranslatedPersonaRankings(personaRankings);
    } finally {
      setIsTranslating(false);
    }
  }, [language, categoryDefinition, perceptions, attributeMappings, personaRankings, translate, category.category_name]);

  // Trigger translation when language or data changes
  useEffect(() => {
    if (categoryDefinition || perceptions.length > 0 || attributeMappings.length > 0 || personaRankings.length > 0) {
      translateContent();
    }
  }, [language, categoryDefinition, perceptions, attributeMappings, personaRankings, translateContent]);

  // DEBUG: Log on every render to see current state
  console.log('[CategoryLeafDetail] COMPONENT RENDER - hasDefinitionResearch:', hasDefinitionResearch, 'perceptions:', perceptions.length, 'personaRankings:', personaRankings.length, 'category:', category.category_name);
  console.log('[CategoryLeafDetail] LOADING STATES - loadingDefinition:', loadingDefinition, 'loadingBenefits:', loadingBenefits, 'loadingAttributes:', loadingAttributes, 'loadingPersonas:', loadingPersonas);
  const [expansionAnalysis, setExpansionAnalysis] = useState<ExpansionAnalysis | null>(null);
  const [loadingExpansion, setLoadingExpansion] = useState(false);
  const [showExpansionResults, setShowExpansionResults] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [editableSubcategories, setEditableSubcategories] = useState<any[]>([]);
  const [creatingCategories, setCreatingCategories] = useState(false);
  const [showAllBenefits, setShowAllBenefits] = useState(false);
  const [researchVersion, setResearchVersion] = useState(0); // Increments when research completes to reset readiness buttons
  const [pendingResearch, setPendingResearch] = useState<any>(null);
  const [loadingPendingResearch, setLoadingPendingResearch] = useState(false);
  const [approvingResearch, setApprovingResearch] = useState(false);

  // AI Enrichment state
  const [enrichmentStats, setEnrichmentStats] = useState<{
    product_count: number;
    enriched_count: number;
  } | null>(null);
  const [loadingEnrichment, setLoadingEnrichment] = useState(false);
  const [batchEnriching, setBatchEnriching] = useState(false);
  const [showBatchProgress, setShowBatchProgress] = useState(false);

  // Category AEO Content state
  const [generatingCategoryAEO, setGeneratingCategoryAEO] = useState(false);
  const [showCategoryAEOModal, setShowCategoryAEOModal] = useState(false);
  const [categoryAEOContent, setCategoryAEOContent] = useState<any>(null);
  const [categoryAEOMetadata, setCategoryAEOMetadata] = useState<any>(null);
  const [savingCategoryAEO, setSavingCategoryAEO] = useState(false);

  // Page type generation state
  const [generatingPageType, setGeneratingPageType] = useState<string | null>(null);
  const [pageTypeResult, setPageTypeResult] = useState<any>(null);
  const [showPageTypeModal, setShowPageTypeModal] = useState(false);
  const [comparisonEntityA, setComparisonEntityA] = useState('');
  const [comparisonEntityB, setComparisonEntityB] = useState('');
  const [showComparisonForm, setShowComparisonForm] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  useEffect(() => {
    // Load data for both sector and site-level categories
    if (isOpen) {
      // Always clear state first to prevent showing stale data
      setHasDefinitionResearch(false);
      setCategoryDefinition('');
      setPerceptions([]);
      setAttributeMappings([]);
      setPersonaRankings([]);
      setHasResearch(false);
      setEnrichmentStats(null);

      // Then load new data
      loadCategoryData();
      // Also load enrichment stats when siteId is available
      if (siteId) {
        loadEnrichmentStats();
      }
    } else {
      // Clear state when panel closes
      setHasDefinitionResearch(false);
      setCategoryDefinition('');
      setPerceptions([]);
      setAttributeMappings([]);
      setPersonaRankings([]);
      setHasResearch(false);
      setEnrichmentStats(null);
    }
  }, [isOpen, category.id, category.category_name, category.full_path, sectorId, siteId]);

  const loadCategoryData = async () => {
    setLoading(true);
    setAttributeMappingsError(null);
    try {
      if (isSiteLevelCategory) {
        // Site-level category: use single endpoint that returns all research
        const { categoryPath, subcategoryName } = getSubcategoryInfo();
        const encodedPath = encodeURIComponent(categoryPath);
        const url = `${API_URL}/sites/${siteId}/categories/${encodedPath}/research${subcategoryName ? `?subcategory=${encodeURIComponent(subcategoryName)}` : ''}`;

        const response = await authFetch(url);
        if (response.ok) {
          const data = await response.json();

          // Map site-level research data to component state
          setHasDefinitionResearch(!!data.definition);
          setCategoryDefinition(data.definition || '');
          setPerceptions(data.benefits || []);
          setAttributeMappings(data.attributes || []);
          setPersonaRankings(data.personaRankings || []);
          setHasResearch(data.benefits?.length > 0 || data.attributes?.length > 0);
          setPendingResearch(null); // Site-level doesn't have pending research concept
        } else {
          // No research yet - reset all
          setHasDefinitionResearch(false);
          setCategoryDefinition('');
          setPerceptions([]);
          setAttributeMappings([]);
          setPersonaRankings([]);
          setHasResearch(false);
          setPendingResearch(null);
        }
      } else {
        // Sector-level category: use original endpoints
        const [rankingsResponse, perceptionsResponse, pendingResponse, mappingsResponse, topicResponse] = await Promise.all([
          authFetch(`${API_URL}/category-templates/${category.id}/persona-rankings?sectorId=${sectorId}`),
          authFetch(`${API_URL}/category-research/${sectorId}/perceptions/${encodeURIComponent(category.category_name)}`),
          authFetch(`${API_URL}/sectors/${sectorId}/categories/${category.id}/pending-research`),
          authFetch(`${API_URL}/sectors/${sectorId}/categories/${category.id}/attribute-mappings${siteId ? `?siteId=${encodeURIComponent(siteId)}` : ''}`),
          authFetch(`${API_URL}/sectors/${sectorId}/topics?search=${encodeURIComponent(category.category_name)}`),
        ]);

        if (rankingsResponse.ok) {
          const rankingsData = await rankingsResponse.json();
          console.log('[CategoryLeafDetail] Rankings response:', rankingsData);
          const rankings = rankingsData.data.persona_rankings || [];
          console.log('[CategoryLeafDetail] Setting persona rankings, count:', rankings.length);
          setPersonaRankings(rankings);
        } else {
          console.log('[CategoryLeafDetail] Rankings response not ok:', rankingsResponse.status);
          setPersonaRankings([]);
        }

        if (perceptionsResponse.ok) {
          const perceptionsData = await perceptionsResponse.json();
          setPerceptions(perceptionsData.perceptions || []);
          setHasResearch(perceptionsData.perceptions && perceptionsData.perceptions.length > 0);
        } else {
          setHasResearch(false);
          setPerceptions([]);
        }

        if (pendingResponse.ok) {
          const pendingData = await pendingResponse.json();
          setPendingResearch(pendingData.data);
        } else {
          setPendingResearch(null);
        }

        if (mappingsResponse.ok) {
          const mappingsData = await mappingsResponse.json();
          setAttributeMappings(mappingsData.data.mappings || []);
        } else {
          setAttributeMappings([]);
          setTranslatedMappings([]);
          const failure = await mappingsResponse.json();
          setAttributeMappingsError(failure.error || `Attribute mappings request failed (${mappingsResponse.status})`);
        }

        // Check if definition research (topic) exists and load the definition text
        if (topicResponse.ok) {
          const topicData = await topicResponse.json();
          console.log('[CategoryLeafDetail] Topics response:', topicData);
          // Topics API returns topic_name, not name
          const matchingTopic = topicData.topics?.find((t: any) =>
            (t.topic_name || t.name)?.toLowerCase() === category.category_name.toLowerCase()
          );
          const hasTopic = !!matchingTopic;
          console.log('[CategoryLeafDetail] hasTopic:', hasTopic, 'matchingTopic:', matchingTopic?.topic_name || matchingTopic?.name);
          setHasDefinitionResearch(hasTopic);

          // Load definition text from topic if exists
          if (matchingTopic) {
            // The topic uses topic_description field
            const definitionText = matchingTopic.topic_description ||
              matchingTopic.description ||
              matchingTopic.customer_facing_description ||
              '';
            setCategoryDefinition(definitionText);
          } else {
            setCategoryDefinition('');
          }
        } else {
          console.log('[CategoryLeafDetail] Topics response not ok:', topicResponse.status);
          setHasDefinitionResearch(false);
          setCategoryDefinition('');
        }
      }
    } catch (error) {
      console.error('Error loading category data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerResearch = async () => {
    if (!onTriggerResearch) return;
    setLoadingResearch(true);
    try {
      await onTriggerResearch(category.category_name);
      // Reload data after research is triggered
      setTimeout(() => {
        loadCategoryData();
        setLoadingResearch(false);
      }, 2000);
    } catch (error) {
      console.error('Error triggering research:', error);
      setLoadingResearch(false);
    }
  };

  // Load AI enrichment stats for this category (when siteId is available)
  const loadEnrichmentStats = async () => {
    if (!siteId) return;

    setLoadingEnrichment(true);
    try {
      const { categoryPath, subcategoryName } = getSubcategoryInfo();
      const encodedPath = encodeURIComponent(categoryPath);

      const response = await authFetch(
        `${API_URL}/product-enrichment/category-stats?siteId=${siteId}&categoryPath=${encodedPath}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.stats) {
          setEnrichmentStats({
            product_count: data.stats.total_products,
            enriched_count: data.stats.enriched_count,
          });
        }
      }
    } catch (error) {
      console.error('Error loading enrichment stats:', error);
    } finally {
      setLoadingEnrichment(false);
    }
  };

  // Handle batch AI enrichment (uses async Batch API for large batches)
  const handleBatchEnrich = async () => {
    if (!siteId || batchEnriching) return;

    setBatchEnriching(true);
    try {
      const { categoryPath } = getSubcategoryInfo();

      toast({
        title: 'Starting Batch Enrichment',
        description: `Enriching products in "${category.category_name}"...`,
      });

      // Use the async batch API endpoint (uses OpenAI Batch API for >20 products)
      // limit: 0 means process ALL products in this category
      const response = await authFetch(`${API_URL}/product-enrichment/batch-enrich-async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: siteId,
          categoryPath: categoryPath,
          limit: 0, // 0 = no limit, process all products in category
        }),
      });

      const result = await response.json();

      // #3906: a PARTIAL split-batch result comes back as success:false at HTTP 207
      // (chunking path: some chunks queued, some blocked) — NOT success:true. It must be
      // handled BEFORE the `if (result.success)` branch, else it falls through to the
      // error path (throw) and the operator never sees "Partially Queued" nor the batch
      // progress, despite jobs having been submitted. (The single-batch partial shape is
      // mode:'batch' + partial:true at success:true; both are caught here.) Mirrors the
      // #3924 fix in TheOffering.product.tsx.
      if (result.mode === 'partial' || result.partial === true) {
        const blockedCats = result.blocked_by_category ? Object.keys(result.blocked_by_category) : [];
        toast({
          title: 'Partially Queued',
          description: result.message
            || `Some products queued; ${result.blocked_count ?? 'some'} blocked by semantic gaps${blockedCats.length ? ` (${blockedCats.join(', ')})` : ''}.`,
        });
        setShowBatchProgress(true);
        return;
      }

      if (result.success) {
        if (result.mode === 'sync') {
          // Sync mode - immediate results
          toast({
            title: 'Batch Enrichment Complete',
            description: result.message,
          });
          // Reload enrichment stats
          loadEnrichmentStats();
        } else if (result.mode === 'batch') {
          // Async batch mode - job queued
          toast({
            title: 'Batch Job Queued',
            description: `${result.total_products} products queued for enrichment. OpenAI will process within 24 hours (50% cost savings).`,
          });
          setShowBatchProgress(true);
        } else if (result.mode === 'none') {
          toast({
            title: 'No Products to Enrich',
            description: result.message,
          });
        }
      } else {
        // #3909 iter-6 / #3962 CR2: a semantic-gap block returns HTTP 409 with
        // { success:false, mode:'blocked'|'blocked_and_failed', message, blocked_by_category }.
        // Reuse computeBlockedOutcome (the same shared decision as TheOffering.product.tsx) so
        // BOTH 'blocked' AND 'blocked_and_failed' (split batch: blocked chunks + genuine
        // failures) surface the actionable 'Enrichment Blocked' title instead of throwing
        // through to the generic Error toast.
        if (result.mode === 'blocked' || result.mode === 'blocked_and_failed') {
          const outcome = computeBlockedOutcome(result);
          toast({ title: outcome.title, description: outcome.description, variant: outcome.variant });
          return;
        }
        throw new Error(result.message || result.error || 'Batch enrichment failed');
      }
    } catch (error: any) {
      console.error('Error in batch enrichment:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to enrich products',
        variant: 'destructive',
      });
    } finally {
      setBatchEnriching(false);
    }
  };

  // Generate Category AEO Content
  const handleGenerateCategoryAEO = async (force: boolean = false) => {
    if (!siteId) {
      toast({
        title: 'Error',
        description: 'Site ID is required to generate AEO content',
        variant: 'destructive',
      });
      return;
    }

    setGeneratingCategoryAEO(true);
    try {
      const response = await authFetch(`${API_URL}/category/aeo-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          categoryPath: category.category_path || category.full_path,
          language: language || 'en',
          force
        })
      });

      const data = await response.json();

      if (data.success && data.data) {
        setCategoryAEOContent(data.data);
        // Set metadata if loading from saved content
        if (data.data.from_cache && data.data.persistence_id) {
          setCategoryAEOMetadata({
            id: data.data.persistence_id,
            version: data.data.version,
            status: data.data.status,
          });
        } else {
          setCategoryAEOMetadata(null);
        }
        setShowCategoryAEOModal(true);

        // Show appropriate toast based on source
        if (data.data.from_cache) {
          const sectionCount = data.data.sections ? Object.keys(data.data.sections).filter(k => data.data.sections[k]).length : 0;
          toast({
            title: 'AEO Content Loaded',
            description: `Loaded saved content (v${data.data.version || 1}, ${sectionCount} sections)`,
          });
        } else {
          toast({
            title: 'AEO Content Generated',
            description: `Quality grade: ${data.data.quality?.grade || 'N/A'} (${data.data.quality?.sections_complete || 0}/${data.data.quality?.sections_total || 0} sections)`,
          });
        }
      } else {
        throw new Error(data.error || 'Failed to generate AEO content');
      }
    } catch (error: any) {
      console.error('Error generating AEO content:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate category AEO content',
        variant: 'destructive',
      });
    } finally {
      setGeneratingCategoryAEO(false);
    }
  };

  // Save Category AEO Content to database
  const handleSaveCategoryAEO = async () => {
    if (!categoryAEOContent || !siteId) return;

    setSavingCategoryAEO(true);
    try {
      const response = await authFetch(`${API_URL}/category/aeo-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          categoryPath: category.category_path || category.full_path,
          language: language || 'en',
          content: categoryAEOContent.sections || categoryAEOContent,
        }),
      });

      const data = await response.json();

      if (data.success && data.data) {
        setCategoryAEOMetadata(data.data);
        toast({
          title: 'AEO Content Saved',
          description: `Version ${data.data.version || 1} saved successfully`,
        });
      } else {
        throw new Error(data.error || 'Failed to save AEO content');
      }
    } catch (error: any) {
      console.error('Error saving AEO content:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save AEO content',
        variant: 'destructive',
      });
    } finally {
      setSavingCategoryAEO(false);
    }
  };

  // Generate page type content (comparison, buying guide, best of)
  const handleGeneratePageType = async (pageType: 'comparison' | 'buying_guide' | 'best_of' | 'use_case' | 'persona', extraParam?: string) => {
    if (!siteId) return;

    setGeneratingPageType(pageType);
    try {
      let endpoint = '';
      let body: any = {
        siteId,
        categoryPath: category.category_path || category.full_path,
        language: language || 'en'
      };

      switch (pageType) {
        case 'comparison':
          if (!comparisonEntityA || !comparisonEntityB) {
            toast({ title: 'Error', description: 'Select two entities to compare', variant: 'destructive' });
            setGeneratingPageType(null);
            return;
          }
          endpoint = '/category/comparison-page';
          body.entityA = comparisonEntityA;
          body.entityB = comparisonEntityB;
          body.comparisonType = 'brand';
          break;
        case 'buying_guide':
          endpoint = '/category/buying-guide';
          break;
        case 'best_of':
          endpoint = '/category/best-of';
          break;
        case 'use_case':
          endpoint = '/category/use-case';
          body.useCase = extraParam;
          break;
        case 'persona':
          endpoint = '/category/persona';
          body.persona = extraParam;
          break;
      }

      const response = await authFetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.success) {
        setPageTypeResult(data);
        setShowPageTypeModal(true);
        setShowComparisonForm(false);
        toast({
          title: `${pageType.replace('_', ' ')} Generated`,
          description: `${pageType === 'comparison' ? `${comparisonEntityA} vs ${comparisonEntityB}` : category.category_name} page created`,
        });
      } else {
        throw new Error(data.error || 'Generation failed');
      }
    } catch (error: any) {
      console.error(`Error generating ${pageType}:`, error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setGeneratingPageType(null);
    }
  };

  // Copy section to clipboard
  const handleCopySection = async (sectionName: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedSection(sectionName);
      setTimeout(() => setCopiedSection(null), 2000);
      toast({
        title: 'Copied',
        description: `${sectionName} copied to clipboard`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  // Research 1: Category Definition
  const handleResearchDefinition = async () => {
    setLoadingDefinition(true);
    try {
      const { categoryPath, subcategoryName } = getSubcategoryInfo();

      let response;
      if (isSiteLevelCategory) {
        // Site-level category research
        const encodedPath = encodeURIComponent(categoryPath);
        response = await authFetch(
          `${API_URL}/sites/${siteId}/categories/${encodedPath}/research-definition`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subcategory: subcategoryName }),
            timeout: 120000 // 2 minute timeout for AI research
          }
        );
      } else {
        // Sector-level category research
        response = await authFetch(
          `${API_URL}/sectors/${sectorId}/categories/${category.id}/research-definition`,
          { method: 'POST', timeout: 120000 } // 2 minute timeout for AI research
        );
      }

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Definition Research Complete!',
          description: `Comprehensive research document created for "${category.category_name}".`,
        });
        loadCategoryData();
        setResearchVersion(v => v + 1); // Reset readiness button to re-check
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to research definition');
      }
    } catch (error: any) {
      console.error('Error researching definition:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to research category definition',
        variant: 'destructive',
      });
    } finally {
      setLoadingDefinition(false);
    }
  };

  // Research 2: Perceived Benefits
  const handleResearchBenefits = async () => {
    if (loadingBenefits) {
      toast({
        title: 'Research In Progress',
        description: 'Benefits research is already running. Please wait...',
        variant: 'default',
      });
      return;
    }

    setLoadingBenefits(true);
    try {
      const { categoryPath, subcategoryName } = getSubcategoryInfo();

      let response;
      if (isSiteLevelCategory) {
        // Site-level category research
        const encodedPath = encodeURIComponent(categoryPath);
        response = await authFetch(
          `${API_URL}/sites/${siteId}/categories/${encodedPath}/research-benefits`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subcategory: subcategoryName }),
            timeout: 120000 // 2 minute timeout for AI research
          }
        );
      } else {
        // Sector-level category research
        response = await authFetch(
          `${API_URL}/sectors/${sectorId}/categories/${category.id}/research-benefits`,
          { method: 'POST', timeout: 120000 } // 2 minute timeout for AI research
        );
      }

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Benefits Research Complete!',
          description: `Generated ${result.benefits?.length || result.data?.attributesGenerated || 0} perceived benefits for "${category.category_name}".`,
        });
        loadCategoryData();
        setResearchVersion(v => v + 1); // Reset readiness button to re-check
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to research benefits');
      }
    } catch (error: any) {
      console.error('Error researching benefits:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to research perceived benefits',
        variant: 'destructive',
      });
    } finally {
      setLoadingBenefits(false);
    }
  };

  // Research 3: Attribute Mappings
  const handleResearchAttributes = async () => {
    // Check if benefits exist first
    if (perceptions.length === 0) {
      toast({
        title: 'Prerequisites Missing',
        description: 'Please run "Benefits" research first. Attributes are mapped to perceived benefits.',
        variant: 'destructive',
      });
      return;
    }

    setLoadingAttributes(true);
    try {
      const { categoryPath, subcategoryName } = getSubcategoryInfo();

      let response;
      if (isSiteLevelCategory) {
        // Site-level category research
        const encodedPath = encodeURIComponent(categoryPath);
        response = await authFetch(
          `${API_URL}/sites/${siteId}/categories/${encodedPath}/research-attributes`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subcategory: subcategoryName }),
            timeout: 120000 // 2 minute timeout for AI research
          }
        );
      } else {
        // Sector-level category research
        response = await authFetch(
          `${API_URL}/sectors/${sectorId}/categories/${category.id}/research-attributes`,
          { method: 'POST', body: JSON.stringify({}), timeout: 120000 } // 2 minute timeout for AI research
        );
      }

      if (response.ok) {
        const result = await response.json();
        toast({
          title: result.success ? 'Attribute Mapping Complete!' : 'Attribute Research Complete!',
          description: result.message || `Mapped ${result.attributes?.length || 0} attributes`,
          variant: result.success ? 'default' : (result.success === false ? 'destructive' : 'default'),
        });
        if (result.success !== false) {
          loadCategoryData();
          setResearchVersion(v => v + 1); // Reset readiness button to re-check
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to map attributes');
      }
    } catch (error: any) {
      console.error('Error mapping attributes:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to map attributes to perceived benefits',
        variant: 'destructive',
      });
    } finally {
      setLoadingAttributes(false);
    }
  };

  // Research 4: Persona Mappings
  const handleResearchPersonas = async () => {
    console.log('[handleResearchPersonas] START - loadingPersonas:', loadingPersonas, 'perceptions:', perceptions.length);

    if (loadingPersonas) {
      console.log('[handleResearchPersonas] Already loading, returning');
      toast({
        title: 'Research In Progress',
        description: 'Persona mapping is already running. Please wait...',
        variant: 'default',
      });
      return;
    }

    // Check if benefits exist first
    if (perceptions.length === 0) {
      console.log('[handleResearchPersonas] No perceptions, showing error');
      toast({
        title: 'Prerequisites Missing',
        description: 'Please run "Benefits" research first. Personas are mapped to perceived benefits.',
        variant: 'destructive',
      });
      return;
    }

    console.log('[handleResearchPersonas] Setting loading state to true');
    setLoadingPersonas(true);

    try {
      const { categoryPath, subcategoryName } = getSubcategoryInfo();

      let response;
      if (isSiteLevelCategory) {
        // Site-level category research
        const encodedPath = encodeURIComponent(categoryPath);
        console.log('[handleResearchPersonas] Site-level - Making API call to:', `${API_URL}/sites/${siteId}/categories/${encodedPath}/research-personas`);
        response = await authFetch(
          `${API_URL}/sites/${siteId}/categories/${encodedPath}/research-personas`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subcategory: subcategoryName }),
            timeout: 120000 // 2 minute timeout for AI research
          }
        );
      } else {
        // Sector-level category research
        console.log('[handleResearchPersonas] Sector-level - Making API call to:', `${API_URL}/sectors/${sectorId}/categories/${category.id}/research-personas`);
        response = await authFetch(
          `${API_URL}/sectors/${sectorId}/categories/${category.id}/research-personas`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
            timeout: 120000 // 2 minute timeout for AI research
          }
        );
      }

      console.log('[handleResearchPersonas] Response received, ok:', response.ok, 'status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('[handleResearchPersonas] Success result:', result);

        // Reload persona rankings to show checkmark
        console.log('[handleResearchPersonas] Reloading category data...');
        await loadCategoryData();
        setResearchVersion(v => v + 1); // Reset readiness button to re-check

        toast({
          title: 'Personas Mapped!',
          description: result.message || `Created ${result.rankings?.length || result.data?.mappingsCreated || 0} persona-benefit rankings.`,
        });

        setLoadingPersonas(false);
        console.log('[handleResearchPersonas] SUCCESS - loading set to false');
      } else {
        const error = await response.json();
        console.error('[handleResearchPersonas] Error response:', error);
        setLoadingPersonas(false);
        throw new Error(error.error || 'Failed to map personas to benefits');
      }
    } catch (error: any) {
      console.error('[handleResearchPersonas] CATCH - Error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to map personas to benefits',
        variant: 'destructive',
      });
      setLoadingPersonas(false);
      console.log('[handleResearchPersonas] ERROR - loading set to false');
    }
  };

  const handleAnalyzeExpansion = async () => {
    setLoadingExpansion(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/categories/${category.id}/analyze-expansion`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to analyze category expansion');
      }

      const result = await response.json();
      setExpansionAnalysis(result.data);
      setShowExpansionResults(true);

      toast({
        title: 'Analysis Complete',
        description: `Score: ${result.data.totalScore}/100 - ${result.data.recommendation.shouldExpand ? 'Expansion recommended' : 'Expansion not recommended'}`,
      });
    } catch (error) {
      console.error('Error analyzing expansion:', error);
      toast({
        title: 'Error',
        description: 'Failed to analyze category expansion',
        variant: 'destructive',
      });
    } finally {
      setLoadingExpansion(false);
    }
  };

  const handleApproveResearch = async () => {
    if (!pendingResearch?.sessionId) return;

    setApprovingResearch(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/research/${pendingResearch.sessionId}/approve`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error('Failed to approve research');
      }

      toast({
        title: 'Research Approved',
        description: 'The research has been approved and added to Product Intelligence',
      });

      // Reload data to clear pending and show in Product Intelligence
      loadCategoryData();
      setResearchVersion(v => v + 1); // Reset readiness button to re-check
    } catch (error) {
      console.error('Error approving research:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve research',
        variant: 'destructive',
      });
    } finally {
      setApprovingResearch(false);
    }
  };

  const handleRejectResearch = async () => {
    if (!pendingResearch?.sessionId) return;

    setApprovingResearch(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/research/${pendingResearch.sessionId}/reject`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error('Failed to reject research');
      }

      toast({
        title: 'Research Rejected',
        description: 'The research has been rejected',
        variant: 'destructive',
      });

      // Reload data to clear pending research
      loadCategoryData();
    } catch (error) {
      console.error('Error rejecting research:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject research',
        variant: 'destructive',
      });
    } finally {
      setApprovingResearch(false);
    }
  };

  const handleOpenReviewDialog = () => {
    if (expansionAnalysis?.suggestedSubcategories) {
      setEditableSubcategories([...expansionAnalysis.suggestedSubcategories]);
      setShowReviewDialog(true);
    }
  };

  const handleRemoveSubcategory = (index: number) => {
    setEditableSubcategories(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateSubcategory = (index: number, field: string, value: any) => {
    setEditableSubcategories(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleCreateSubcategories = async () => {
    if (editableSubcategories.length === 0) {
      toast({
        title: 'No Categories',
        description: 'Please add at least one subcategory',
        variant: 'destructive',
      });
      return;
    }

    setCreatingCategories(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/categories/${category.id}/create-subcategories`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            subcategories: editableSubcategories,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create subcategories');
      }

      const result = await response.json();

      toast({
        title: 'Success!',
        description: `Created ${result.data.created_count} subcategories under "${category.category_name}"`,
      });

      setShowReviewDialog(false);
      setShowExpansionResults(false);
      onClose(); // Close the detail panel to show the updated tree

      // Trigger tree reload without full page refresh
      if (onCategoriesCreated) {
        onCategoriesCreated();
      }

    } catch (error) {
      console.error('Error creating subcategories:', error);
      toast({
        title: 'Error',
        description: 'Failed to create subcategories',
        variant: 'destructive',
      });
    } finally {
      setCreatingCategories(false);
    }
  };

  const getTopBenefits = () => {
    // Use translated perceptions if available
    const benefitsToUse = translatedPerceptions.length > 0 ? translatedPerceptions : perceptions;
    if (benefitsToUse.length === 0) return [];

    // Group by benefit layer and take top 3
    const functional = benefitsToUse.filter(p => p.benefit_layer === 'functional').slice(0, 2);
    const emotional = benefitsToUse.filter(p => p.benefit_layer === 'emotional').slice(0, 2);
    const aspirational = benefitsToUse.filter(p => p.benefit_layer === 'aspirational').slice(0, 1);

    return [...functional, ...emotional, ...aspirational];
  };

  const getBenefitLayerColor = (layer: string) => {
    switch (layer) {
      case 'functional': return 'text-blue-600 bg-blue-50';
      case 'emotional': return 'text-red-600 bg-red-50';
      case 'aspirational': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getBenefitLayerEmoji = (layer: string) => {
    switch (layer) {
      case 'functional': return '🧠';
      case 'emotional': return '❤️';
      case 'aspirational': return '🌍';
      default: return '💡';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-out Panel */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[600px] lg:w-[700px] bg-background border-l shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b bg-muted/30">
          <div className="flex-1 pr-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">{category.category_name}</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-3">{category.category_path}</p>
            {/* View Products Button and Enrichment Stats - show when siteId is available */}
            {siteId && (
              <div className="space-y-3">
                {/* Only show "View Products in Catalog" in offering context */}
                {context === 'offering' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const { categoryPath, subcategoryName } = getSubcategoryInfo();
                      // For site-specific subcategories, filter by subcategory field
                      // For Google categories, filter by category field (using the last part of the path)
                      if (subcategoryName) {
                        // Site-specific subcategory - filter by subcategory field
                        onNavigateToTab('products', { subcategory: subcategoryName });
                      } else {
                        // Google category - use the category name for filtering
                        const categoryName = category.category_name || category.category_path?.split(' > ').pop();
                        onNavigateToTab('products', { categoryPath: categoryName });
                      }
                    }}
                    className="flex items-center gap-2"
                  >
                    <ShoppingBag className="h-4 w-4" />
                    View Products in Catalog
                    {enrichmentStats && (
                      <Badge variant="secondary" className="ml-1">
                        {enrichmentStats.product_count.toLocaleString()}
                      </Badge>
                    )}
                  </Button>
                )}

                {/* Batch Enrichment Actions */}
                {enrichmentStats && enrichmentStats.product_count > 0 && (
                  <div className="flex items-center gap-2">
                    {/* Enrich Products Button - shows when there are unenriched products */}
                    {enrichmentStats.enriched_count < enrichmentStats.product_count && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBatchEnrich}
                        disabled={batchEnriching}
                        className="flex items-center gap-2"
                      >
                        {batchEnriching ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4" />
                        )}
                        {batchEnriching ? 'Enriching...' : 'Enrich Products'}
                        <Badge variant="secondary" className="ml-1">
                          {enrichmentStats.product_count - enrichmentStats.enriched_count}
                        </Badge>
                      </Button>
                    )}
                    {/* View Jobs Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowBatchProgress(!showBatchProgress)}
                      className="flex items-center gap-2"
                    >
                      <Clock className="h-4 w-4" />
                      {showBatchProgress ? 'Hide Jobs' : 'View Jobs'}
                    </Button>
                  </div>
                )}

                {/* AI Enrichment Stats (informational only) */}
                {enrichmentStats && enrichmentStats.product_count > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles className={`h-4 w-4 ${
                      enrichmentStats.enriched_count === enrichmentStats.product_count
                        ? 'text-green-500'
                        : enrichmentStats.enriched_count > 0
                        ? 'text-amber-500'
                        : 'text-muted-foreground'
                    }`} />
                    <span>AI Enriched:</span>
                    <span className={`font-medium ${
                      enrichmentStats.enriched_count === enrichmentStats.product_count
                        ? 'text-green-600'
                        : enrichmentStats.enriched_count > 0
                        ? 'text-amber-600'
                        : ''
                    }`}>
                      {enrichmentStats.enriched_count}/{enrichmentStats.product_count}
                      {' '}({Math.round((enrichmentStats.enriched_count / enrichmentStats.product_count) * 100)}%)
                    </span>
                  </div>
                )}

                {/* Loading state for enrichment stats */}
                {loadingEnrichment && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading enrichment stats...
                  </div>
                )}

                {/* Batch enrichment progress (shown when batch jobs exist) */}
                {siteId && showBatchProgress && (
                  <EnrichmentBatchProgress
                    siteId={siteId}
                    onJobComplete={() => {
                      loadEnrichmentStats();
                    }}
                  />
                )}

                {/* Sector Readiness Check - always shown to guide sector research */}
                <SectorReadinessButton
                  key={`sector-readiness-${researchVersion}`}
                  sectorId={sectorId}
                  categoryPath={category.full_path || category.category_path}
                  variant="outline"
                  size="sm"
                />

                {/* Site-specific buttons - only in offering context */}
                {context === 'offering' && (
                  <>
                    {/* Site AEO Readiness Check */}
                    <AEOReadinessButton
                      siteId={siteId}
                      categoryPath={category.full_path || category.category_path}
                      variant="outline"
                      size="sm"
                    />

                    {/* Site AEO Content Generation Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGenerateCategoryAEO()}
                      disabled={generatingCategoryAEO}
                      className="flex items-center gap-2 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 border-purple-200 dark:border-purple-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-purple-950/50 dark:hover:to-blue-950/50"
                    >
                      {generatingCategoryAEO ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                      )}
                      <span className="text-purple-700 dark:text-purple-300">
                        {generatingCategoryAEO ? 'Generating...' : 'AEO Content'}
                      </span>
                    </Button>

                    {/* Page Type Buttons */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGeneratePageType('buying_guide')}
                      disabled={!!generatingPageType}
                      className="flex items-center gap-1.5 text-xs"
                    >
                      {generatingPageType === 'buying_guide' ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <FileText className="h-3 w-3" />
                      )}
                      Buying Guide
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGeneratePageType('best_of')}
                      disabled={!!generatingPageType}
                      className="flex items-center gap-1.5 text-xs"
                    >
                      {generatingPageType === 'best_of' ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Target className="h-3 w-3" />
                      )}
                      Best Of
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowComparisonForm(!showComparisonForm)}
                      disabled={!!generatingPageType}
                      className="flex items-center gap-1.5 text-xs"
                    >
                      <ChevronRight className="h-3 w-3" />
                      Compare
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const useCase = prompt('Enter use case (e.g., "for flat feet", "for marathon"):');
                        if (useCase) handleGeneratePageType('use_case', useCase);
                      }}
                      disabled={!!generatingPageType}
                      className="flex items-center gap-1.5 text-xs"
                    >
                      {generatingPageType === 'use_case' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Users className="h-3 w-3" />}
                      Use Case
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const persona = prompt('Enter persona (e.g., "beginner", "professional", "budget-conscious"):');
                        if (persona) handleGeneratePageType('persona', persona);
                      }}
                      disabled={!!generatingPageType}
                      className="flex items-center gap-1.5 text-xs"
                    >
                      {generatingPageType === 'persona' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShoppingBag className="h-3 w-3" />}
                      Persona
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Comparison Form (inline, before content) */}
        {showComparisonForm && context === 'offering' && siteId && (
          <div className="mx-6 mt-2 mb-0 p-3 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50/50 dark:bg-blue-950/20">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium">Compare brands in {category.category_name}:</span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Brand A (e.g. Continental)"
                value={comparisonEntityA}
                onChange={(e) => setComparisonEntityA(e.target.value)}
                className="h-8 text-sm"
              />
              <span className="text-sm text-muted-foreground">vs</span>
              <Input
                placeholder="Brand B (e.g. Schwalbe)"
                value={comparisonEntityB}
                onChange={(e) => setComparisonEntityB(e.target.value)}
                className="h-8 text-sm"
              />
              <Button
                size="sm"
                onClick={() => handleGeneratePageType('comparison')}
                disabled={!!generatingPageType || !comparisonEntityA || !comparisonEntityB}
                className="shrink-0"
              >
                {generatingPageType === 'comparison' ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : null}
                Generate
              </Button>
            </div>
          </div>
        )}

        {attributeMappingsError && <div role="alert" className="mx-6 mt-2 text-sm text-red-600">{attributeMappingsError}</div>}
        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Loading category details...</span>
            </div>
          ) : (
            <div className="space-y-6 max-w-full">
              {/* Definition & Research Section */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-base">Definition & Research</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Show definition preview if available (translated if language is not English) */}
                  {hasDefinitionResearch && categoryDefinition && (
                    <DefinitionPreview
                      definition={translatedDefinition || categoryDefinition}
                      categoryName={category.category_name}
                      isSiteLevel={isSiteLevelCategory}
                    />
                  )}

                  {/* Show status message and action buttons only if research exists */}
                  {hasResearch && (
                    <>
                      <p className="text-sm text-muted-foreground">
                        This category has been researched with {perceptions.length} perceived attributes
                        identified across functional, emotional, and aspirational benefit layers.
                      </p>
                      {!isSiteLevelCategory && (
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Open Master Topic Approvals in new tab filtered for this category
                              window.open(`/admin/topics?search=${encodeURIComponent(category.category_name)}`, '_blank');
                            }}
                            className="flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            View Research Topics
                          </Button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Independent Research Triggers - ALWAYS VISIBLE */}
                  <div className={hasResearch ? "mt-4 space-y-2" : "space-y-2"}>
                        <p className="text-sm font-medium text-muted-foreground">Research Actions:</p>

                        {/* Show message for site-level categories */}
                        {isSiteLevelCategory && (
                          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                            <p className="text-blue-900 dark:text-blue-100">
                              Site-level category: Research will be specific to {category.category_name} products on this site.
                            </p>
                          </div>
                        )}

                        {/* Role-based access notice */}
                        {!isSuperAdmin && hasResearch && (
                          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm">
                            <p className="text-amber-900 dark:text-amber-100">
                              Research data exists. Only Super Admins can refresh existing research.
                            </p>
                          </div>
                        )}

                        <div className="flex flex-col gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResearchDefinition}
                            disabled={loadingDefinition || (hasDefinitionResearch && !isSuperAdmin)}
                            className="flex items-center justify-between gap-2"
                            title={hasDefinitionResearch && !isSuperAdmin ? 'Only Super Admins can refresh existing research' : undefined}
                          >
                            {loadingDefinition ? (
                              <>
                                <div className="flex items-center gap-1">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Researching...
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-1">
                                  <FileText className="h-4 w-4" />
                                  Definition
                                </div>
                                {(() => {
                                  console.log('[CategoryLeafDetail] Rendering Definition button, hasDefinitionResearch:', hasDefinitionResearch);
                                  return hasDefinitionResearch && <Check className="h-4 w-4 text-green-600" />;
                                })()}
                              </>
                            )}
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResearchBenefits}
                            disabled={loadingBenefits || (perceptions.length > 0 && !isSuperAdmin)}
                            className="flex items-center justify-between gap-2"
                            title={perceptions.length > 0 && !isSuperAdmin ? 'Only Super Admins can refresh existing research' : undefined}
                          >
                            {loadingBenefits ? (
                              <>
                                <div className="flex items-center gap-1">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Researching...
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-1">
                                  <Sparkles className="h-4 w-4" />
                                  Benefits
                                </div>
                                {perceptions.length > 0 && <Check className="h-4 w-4 text-green-600" />}
                              </>
                            )}
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResearchAttributes}
                            disabled={loadingAttributes || (attributeMappings.length > 0 && !isSuperAdmin)}
                            className="flex items-center justify-between gap-2"
                            title={attributeMappings.length > 0 && !isSuperAdmin ? 'Only Super Admins can refresh existing research' : undefined}
                          >
                            {loadingAttributes ? (
                              <>
                                <div className="flex items-center gap-1">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Mapping...
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-1">
                                  <Link2 className="h-4 w-4" />
                                  Attributes
                                </div>
                                {attributeMappings.length > 0 && <Check className="h-4 w-4 text-green-600" />}
                              </>
                            )}
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResearchPersonas}
                            disabled={loadingPersonas || (personaRankings.length > 0 && !isSuperAdmin)}
                            className="flex items-center justify-between gap-2"
                            title={personaRankings.length > 0 && !isSuperAdmin ? 'Only Super Admins can refresh existing research' : undefined}
                          >
                            {loadingPersonas ? (
                              <>
                                <div className="flex items-center gap-1">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Mapping...
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-1">
                                  <Users className="h-4 w-4" />
                                  Personas
                                </div>
                                {personaRankings.length > 0 && <Check className="h-4 w-4 text-green-600" />}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                </CardContent>
              </Card>

              {/* Pending Research Review Section */}
              {pendingResearch && pendingResearch.benefits && pendingResearch.benefits.length > 0 && (
                <Card className="border-amber-500 bg-amber-50/50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-amber-600" />
                        <CardTitle className="text-base text-amber-900">Review Research Results</CardTitle>
                      </div>
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                        {pendingResearch.attributesCount} benefits
                      </Badge>
                    </div>
                    <CardDescription className="text-amber-700">
                      Review and approve these AI-generated perceived benefits before adding them to Product Intelligence
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="max-h-[500px] overflow-y-auto pr-2 space-y-2">
                      {pendingResearch.benefits.map((benefit: any, index: number) => (
                        <div key={index} className="border-l-4 border-amber-500 pl-3 py-2 bg-white rounded-r mb-2">
                          <div className="flex items-start gap-2">
                            <span className="text-lg">{getBenefitLayerEmoji(benefit.benefit_layer)}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h4 className="font-medium text-sm break-words">{benefit.attribute_name}</h4>
                                <Badge variant="secondary" className="text-xs">
                                  {benefit.benefit_layer}
                                </Badge>
                                {benefit.impact_score && (
                                  <span className="text-xs text-muted-foreground">
                                    Impact: {benefit.impact_score}/10
                                  </span>
                                )}
                              </div>
                              {benefit.reasoning && (
                                <p className="text-sm text-muted-foreground mb-2 break-words">{benefit.reasoning}</p>
                              )}
                              {benefit.example_phrases && benefit.example_phrases.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {benefit.example_phrases.map((phrase: string, idx: number) => (
                                    <code key={idx} className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                      "{phrase}"
                                    </code>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        onClick={handleApproveResearch}
                        disabled={approvingResearch}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        {approvingResearch ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Approving...
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Approve & Add to Product Intelligence
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={handleRejectResearch}
                        disabled={approvingResearch}
                        variant="destructive"
                      >
                        {approvingResearch ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Perceived Benefits Section */}
              {hasResearch && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-600" />
                        <CardTitle className="text-base">Top Perceived Benefits</CardTitle>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllBenefits(!showAllBenefits)}
                        className="text-xs"
                      >
                        {showAllBenefits ? 'Show Less' : `See All (${perceptions.length})`}
                        <ChevronRight className={`h-3 w-3 ml-1 transition-transform ${showAllBenefits ? 'rotate-90' : ''}`} />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(showAllBenefits ? (translatedPerceptions.length > 0 ? translatedPerceptions : perceptions) : getTopBenefits()).map((benefit, index) => (
                      <div key={index} className="border-l-4 border-primary pl-3 py-2 bg-muted/30 rounded-r">
                        <div className="flex items-start gap-2">
                          <span className="text-lg">{getBenefitLayerEmoji(benefit.benefit_layer)}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h4 className="font-medium text-sm break-words">{benefit.attribute_name}</h4>
                              <Badge variant="secondary" className={`text-xs ${getBenefitLayerColor(benefit.benefit_layer)}`}>
                                {benefit.benefit_layer}
                              </Badge>
                              {benefit.purchase_driver_strength > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  Impact: {benefit.purchase_driver_strength}/10
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground break-words">
                              {benefit.customer_description}
                            </p>
                            {benefit.common_phrases && benefit.common_phrases.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {benefit.common_phrases.slice(0, 3).map((phrase, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    "{phrase}"
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Product Intelligence: Attribute Mappings Section */}
              {attributeMappings.length > 0 && (
                <Card className="border-blue-200 bg-blue-50/30 dark:border-blue-900 dark:bg-blue-950/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <CardTitle className="text-base text-blue-900 dark:text-blue-100">Product Intelligence: Attribute Mappings</CardTitle>
                      </div>
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700">
                        {attributeMappings.length} mappings
                      </Badge>
                    </div>
                    <CardDescription className="text-blue-700 dark:text-blue-300">
                      How technical product attributes translate to customer benefits
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(translatedMappings.length > 0 ? translatedMappings : attributeMappings)
                        .sort((a, b) => {
                          const impactOrder = { high: 1, medium: 2, low: 3 };
                          return (impactOrder[a.impact_level] - impactOrder[b.impact_level]) ||
                                 (b.confidence_score - a.confidence_score);
                        })
                        .map((mapping, index) => (
                        <div key={mapping.id} className="border-l-4 border-blue-500 dark:border-blue-400 pl-3 py-2 bg-white dark:bg-slate-800 rounded-r">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              {/* Technical attribute and arrow on first line */}
                              <div className="flex items-center gap-2 mb-1">
                                <code className="text-xs font-mono bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded whitespace-nowrap">
                                  {mapping.technical_attribute}
                                  {mapping.technical_value_range && (
                                    <span className="text-gray-500 dark:text-gray-400 ml-1">= {mapping.technical_value_range}</span>
                                  )}
                                </code>
                                <span className="text-gray-400 dark:text-gray-500">→</span>
                              </div>

                              {/* Benefit name on second line */}
                              <div className="font-medium text-sm mb-1 break-words">
                                {mapping.perceived_attribute_name}
                              </div>

                              {/* Badges on third line, wrapping */}
                              <div className="flex items-center gap-1 mb-2 flex-wrap">
                                <Badge
                                  variant="secondary"
                                  className={`text-xs ${
                                    mapping.impact_level === 'high'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : mapping.impact_level === 'medium'
                                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                                  }`}
                                >
                                  {mapping.impact_level}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {Math.round(mapping.confidence_score * 100)}%
                                </Badge>
                                <Badge variant="secondary" className={`text-xs ${
                                  mapping.benefit_layer === 'functional' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                  mapping.benefit_layer === 'emotional' ? 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200' :
                                  'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                }`}>
                                  {mapping.benefit_layer}
                                </Badge>
                              </div>

                              {/* Description on last line */}
                              <p className="text-xs text-gray-600 dark:text-gray-400 break-words">{mapping.impact_description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Persona Rankings Section */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-green-600" />
                      <CardTitle className="text-base">Buyer Persona Rankings</CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-xs">
                    Personas ranked by priority for this category
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {personaRankings.length > 0 ? (
                    <div className="space-y-3">
                      {(translatedPersonaRankings.length > 0 ? translatedPersonaRankings : personaRankings).slice(0, 5).map((persona, index) => (
                        <div
                          key={persona.persona_name}
                          className="flex items-center gap-3 p-3 border rounded-lg"
                        >
                          {/* Rank Badge */}
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                            {index + 1}
                          </div>

                          {/* Avatar */}
                          <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden bg-muted">
                            <img
                              src={persona.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(persona.persona_name)}&background=random&size=100`}
                              alt={persona.persona_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(persona.persona_name)}&background=8B5CF6&color=fff&size=100`;
                              }}
                            />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-sm truncate">{persona.persona_name}</h4>
                              {persona.age_range && (
                                <Badge variant="outline" className="text-xs flex-shrink-0">
                                  {persona.age_range}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              {persona.occupation || persona.persona_description}
                            </p>
                          </div>

                          {/* Metrics */}
                          <div className="flex-shrink-0 text-right">
                            <div className="text-sm font-semibold text-primary">
                              {Math.round(persona.avg_priority_weight * 100)}%
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {persona.benefits_count} benefits
                            </div>
                          </div>

                          {/* Generate persona page */}
                          {context === 'offering' && siteId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="flex-shrink-0 h-7 px-2 text-xs"
                              disabled={!!generatingPageType}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGeneratePageType('persona', typeof persona.persona_name === 'object' ? (persona.persona_name as any)?.en || Object.values(persona.persona_name)[0] : persona.persona_name);
                              }}
                              title={`Generate "Best ${category.category_name} for ${persona.persona_name}" page`}
                            >
                              {generatingPageType === 'persona' ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Sparkles className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                        </div>
                      ))}

                    </div>
                  ) : (
                    <Alert>
                      <AlertDescription className="text-sm">
                        No persona mappings found for this category. Run "AI Auto-Map Benefits" from the
                        Personas tab to generate priority rankings.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Content Generation CTA — appears when research is available */}
              {context === 'offering' && siteId && (hasDefinitionResearch || perceptions.length > 0 || personaRankings.length > 0) && (
                <Card className="border-green-200 dark:border-green-800 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-green-600" />
                      <CardTitle className="text-base">Generate Category Content</CardTitle>
                    </div>
                    <CardDescription className="text-xs">
                      Research available — create AEO content pages based on this intelligence
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-white dark:bg-gray-900"
                        disabled={!!generatingPageType || generatingCategoryAEO}
                        onClick={() => handleGenerateCategoryAEO()}
                      >
                        {generatingCategoryAEO ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Zap className="h-3 w-3 mr-1 text-purple-600" />}
                        Landing Page
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-white dark:bg-gray-900"
                        disabled={!!generatingPageType}
                        onClick={() => handleGeneratePageType('buying_guide')}
                      >
                        {generatingPageType === 'buying_guide' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FileText className="h-3 w-3 mr-1" />}
                        Buying Guide
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-white dark:bg-gray-900"
                        disabled={!!generatingPageType}
                        onClick={() => handleGeneratePageType('best_of')}
                      >
                        {generatingPageType === 'best_of' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Target className="h-3 w-3 mr-1" />}
                        Best Of
                      </Button>
                      {personaRankings.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white dark:bg-gray-900"
                          disabled={!!generatingPageType}
                          onClick={() => {
                            const pName = personaRankings[0]?.persona_name;
                            const name = typeof pName === 'object' ? (pName as any)?.en || Object.values(pName)[0] : pName;
                            handleGeneratePageType('persona', name as string);
                          }}
                        >
                          {generatingPageType === 'persona' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Users className="h-3 w-3 mr-1" />}
                          For {typeof personaRankings[0]?.persona_name === 'object' ? (personaRankings[0]?.persona_name as any)?.en || Object.values(personaRankings[0]?.persona_name)[0] : personaRankings[0]?.persona_name}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-white dark:bg-gray-900"
                        disabled={!!generatingPageType}
                        onClick={() => setShowComparisonForm(!showComparisonForm)}
                      >
                        <ChevronRight className="h-3 w-3 mr-1" />
                        Compare Brands
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* AI Expansion Analysis Section - only for leaf nodes (categories without children) */}
              {!category.hasChildren && (
              <Card className="border-purple-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wand2 className="h-5 w-5 text-purple-600" />
                      <CardTitle className="text-base">AI Category Expansion</CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-xs">
                    Analyze if this category should be subdivided with AI-suggested subcategories
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!showExpansionResults ? (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Use AI to analyze if "{category.category_name}" would benefit from subdivision into more specific subcategories based on industry standards, search patterns, and product diversity.
                      </p>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleAnalyzeExpansion}
                        disabled={loadingExpansion}
                        className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                      >
                        {loadingExpansion ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Wand2 className="h-4 w-4" />
                            Analyze Category
                          </>
                        )}
                      </Button>
                    </div>
                  ) : expansionAnalysis ? (
                    <div className="space-y-4">
                      {/* Score Summary */}
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div>
                          <div className="text-2xl font-bold text-purple-600">
                            {expansionAnalysis.totalScore}/100
                          </div>
                          <div className="text-xs text-muted-foreground">Expansion Score</div>
                        </div>
                        <Badge
                          variant={
                            expansionAnalysis.recommendation.priority === 'high'
                              ? 'default'
                              : expansionAnalysis.recommendation.priority === 'medium'
                              ? 'secondary'
                              : 'outline'
                          }
                          className={
                            expansionAnalysis.recommendation.priority === 'high'
                              ? 'bg-purple-600'
                              : ''
                          }
                        >
                          {expansionAnalysis.recommendation.priority.toUpperCase()} PRIORITY
                        </Badge>
                      </div>

                      {/* Recommendation */}
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-sm text-blue-900 mb-1">
                              {expansionAnalysis.recommendation.shouldExpand
                                ? 'Expansion Recommended'
                                : 'Expansion Not Recommended'}
                            </div>
                            <p className="text-xs text-blue-700">
                              {expansionAnalysis.analysis.reasoning}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Score Breakdown */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">Score Breakdown</h4>
                        {Object.entries(expansionAnalysis.scores).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <span className="font-medium">{value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Evidence */}
                      {expansionAnalysis.analysis.evidence.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium">Evidence</h4>
                          <ul className="space-y-1">
                            {expansionAnalysis.analysis.evidence.map((item, index) => (
                              <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                                <span className="text-green-600">✓</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Suggested Subcategories */}
                      {expansionAnalysis.suggestedSubcategories.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium">
                            Suggested Subcategories ({expansionAnalysis.suggestedSubcategories.length})
                          </h4>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {expansionAnalysis.suggestedSubcategories.map((subcat, index) => (
                              <div
                                key={index}
                                className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                              >
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <h5 className="font-medium text-sm">{subcat.name}</h5>
                                  <Badge variant="outline" className="text-xs">
                                    {Math.round(subcat.confidence * 100)}% conf.
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mb-2">
                                  {subcat.description}
                                </p>
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="text-xs" title="Relative size estimate within parent category">
                                    {Math.round(subcat.marketShare * 100)}% relative share
                                  </Badge>
                                  {subcat.keyAttributes && subcat.keyAttributes.length > 0 && (
                                    <span className="text-xs text-muted-foreground">
                                      {subcat.keyAttributes.slice(0, 2).join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        {expansionAnalysis.recommendation.shouldExpand &&
                         expansionAnalysis.suggestedSubcategories.length > 0 && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={handleOpenReviewDialog}
                            className="flex-1 bg-purple-600 hover:bg-purple-700"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create {expansionAnalysis.suggestedSubcategories.length} Subcategories
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowExpansionResults(false)}
                          className={expansionAnalysis.recommendation.shouldExpand ? '' : 'w-full'}
                        >
                          Run New Analysis
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Review & Create Subcategories Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Review & Create Subcategories</DialogTitle>
            <DialogDescription>
              Review the AI-suggested subcategories for "{category.category_name}". You can edit names and descriptions or remove suggestions before creating them.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2 -mr-2" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            <div className="space-y-3 py-2">
              {editableSubcategories.map((subcat, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg space-y-3 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 space-y-3">
                      {/* Name Input */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                          Category Name
                        </label>
                        <Input
                          value={subcat.name}
                          onChange={(e) => handleUpdateSubcategory(index, 'name', e.target.value)}
                          placeholder="Enter category name"
                          className="font-medium"
                        />
                      </div>

                      {/* Description Textarea */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                          Description
                        </label>
                        <Textarea
                          value={subcat.description}
                          onChange={(e) => handleUpdateSubcategory(index, 'description', e.target.value)}
                          placeholder="Enter category description"
                          rows={2}
                          className="text-sm"
                        />
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center gap-3 text-xs">
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          {Math.round(subcat.confidence * 100)}% confidence
                        </Badge>
                        <Badge variant="secondary" title="Relative size estimate within parent category">
                          {Math.round(subcat.marketShare * 100)}% relative share
                        </Badge>
                        {subcat.keyAttributes && subcat.keyAttributes.length > 0 && (
                          <span className="text-muted-foreground">
                            {subcat.keyAttributes.slice(0, 3).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Remove Button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveSubcategory(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {editableSubcategories.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No subcategories selected. Close this dialog to start over.</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4 border-t bg-background">
            <div className="w-full mb-2">
              <Alert className="py-2">
                <AlertDescription className="text-xs">
                  <strong>Note:</strong> Market share and confidence scores are AI-estimated based on industry knowledge, not actual market research data.
                </AlertDescription>
              </Alert>
            </div>
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-muted-foreground">
                {editableSubcategories.length} {editableSubcategories.length === 1 ? 'category' : 'categories'} to create
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowReviewDialog(false)}
                  disabled={creatingCategories}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateSubcategories}
                  disabled={creatingCategories || editableSubcategories.length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {creatingCategories ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create {editableSubcategories.length} {editableSubcategories.length === 1 ? 'Category' : 'Categories'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Type Result Dialog */}
      <Dialog open={showPageTypeModal} onOpenChange={setShowPageTypeModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>
              {pageTypeResult?.page_type === 'comparison' && `${pageTypeResult?.entities?.[0]} vs ${pageTypeResult?.entities?.[1]}`}
              {pageTypeResult?.page_type === 'buying_guide' && `Buying Guide: ${category.category_name}`}
              {pageTypeResult?.page_type === 'best_of' && `Best of: ${category.category_name}`}
              {pageTypeResult?.page_type === 'use_case' && `${category.category_name}: ${pageTypeResult?.use_case || ''}`}
              {pageTypeResult?.page_type === 'persona' && `${category.category_name} for ${pageTypeResult?.persona || ''}`}
            </DialogTitle>
            <DialogDescription>
              Page type: {pageTypeResult?.page_type?.replace(/_/g, ' ')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
            {pageTypeResult?.featured_snippet && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                <p className="text-sm font-medium mb-1">Featured Snippet</p>
                <p className="text-sm">{pageTypeResult.featured_snippet}</p>
              </div>
            )}
            {pageTypeResult?.title && (
              <h3 className="text-lg font-semibold">{pageTypeResult.title}</h3>
            )}
            {pageTypeResult?.introduction && (
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{pageTypeResult.introduction}</p>
            )}
            {pageTypeResult?.summary && (
              <p className="text-sm whitespace-pre-wrap">{pageTypeResult.summary}</p>
            )}

            {/* Buying guide: key factors */}
            {pageTypeResult?.key_factors?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Key Factors</p>
                <div className="space-y-2">
                  {pageTypeResult.key_factors.map((factor: any, i: number) => (
                    <div key={i} className="p-3 border rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{factor.factor}</span>
                        <Badge variant={factor.importance === 'high' || factor.importance === 'critical' ? 'default' : 'secondary'} className="text-xs">{factor.importance}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{factor.explanation}</p>
                      {factor.what_to_look_for && <p className="text-xs mt-1"><strong>Look for:</strong> {factor.what_to_look_for}</p>}
                      {factor.budget_recommendation && <p className="text-xs mt-0.5"><strong>Budget:</strong> {factor.budget_recommendation}</p>}
                      {factor.premium_recommendation && <p className="text-xs mt-0.5"><strong>Premium:</strong> {factor.premium_recommendation}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Buying guide: buyer profiles */}
            {pageTypeResult?.buyer_profiles?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Buyer Profiles</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {pageTypeResult.buyer_profiles.map((profile: any, i: number) => (
                    <div key={i} className="p-3 border rounded-lg">
                      <p className="font-medium text-sm mb-1">{profile.profile}</p>
                      {profile.budget_range && <p className="text-xs text-muted-foreground">Budget: {profile.budget_range}</p>}
                      {profile.key_advice && <p className="text-xs mt-1">{profile.key_advice}</p>}
                      {profile.recommended_brands?.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {profile.recommended_brands.map((b: string, j: number) => <Badge key={j} variant="outline" className="text-xs">{b}</Badge>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Buying guide: price tiers */}
            {pageTypeResult?.price_tier_guide && (
              <div>
                <p className="text-sm font-semibold mb-2">Price Tiers</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {Object.entries(pageTypeResult.price_tier_guide).map(([tier, data]: [string, any]) => (
                    <div key={tier} className="p-3 border rounded-lg">
                      <p className="font-medium text-sm capitalize mb-1">{tier.replace(/_/g, ' ')}</p>
                      {data.range && <p className="text-xs text-muted-foreground">{data.range}</p>}
                      {data.what_to_expect && <p className="text-xs mt-1">{data.what_to_expect}</p>}
                      {data.best_for && <p className="text-xs mt-0.5"><strong>Best for:</strong> {data.best_for}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Common mistakes */}
            {pageTypeResult?.common_mistakes?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Common Mistakes</p>
                <ul className="list-disc pl-5 space-y-1">
                  {pageTypeResult.common_mistakes.map((m: string, i: number) => <li key={i} className="text-sm">{m}</li>)}
                </ul>
              </div>
            )}

            {/* Decision checklist */}
            {pageTypeResult?.decision_checklist?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Decision Checklist</p>
                <ul className="space-y-1">
                  {pageTypeResult.decision_checklist.map((item: string, i: number) => (
                    <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Comparison table */}
            {pageTypeResult?.comparison_table?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Comparison</p>
                <div className="overflow-x-auto border rounded">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="p-2 text-left border-b">Attribute</th>
                        <th className="p-2 text-left border-b">{pageTypeResult?.entities?.[0]}</th>
                        <th className="p-2 text-left border-b">{pageTypeResult?.entities?.[1]}</th>
                        <th className="p-2 text-left border-b">Winner</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageTypeResult.comparison_table.map((row: any, i: number) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="p-2 font-medium">{row.attribute}</td>
                          <td className="p-2">{row.entity_a}</td>
                          <td className="p-2">{row.entity_b}</td>
                          <td className="p-2">{row.winner}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Verdict by use case */}
            {pageTypeResult?.verdict_by_use_case?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Verdict by Use Case</p>
                {pageTypeResult.verdict_by_use_case.map((v: any, i: number) => (
                  <div key={i} className="flex items-start gap-2 mb-2 text-sm">
                    <Badge variant="outline" className="flex-shrink-0 mt-0.5">{v.winner}</Badge>
                    <div><strong>{v.use_case}:</strong> {v.reason}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Rankings (best-of) */}
            {pageTypeResult?.rankings?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Rankings</p>
                {pageTypeResult.rankings.map((item: any, i: number) => (
                  <div key={i} className="mb-3 p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline">#{item.rank}</Badge>
                      <span className="font-medium">{item.product_title}</span>
                      {item.award && <Badge variant="secondary" className="text-xs">{item.award}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{item.why_chosen}</p>
                    {item.pros?.length > 0 && <p className="text-xs mt-1 text-green-700">+ {item.pros.join(' | ')}</p>}
                    {item.cons?.length > 0 && <p className="text-xs text-red-700">- {item.cons.join(' | ')}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Use case: requirements + recommended products */}
            {pageTypeResult?.key_requirements?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Key Requirements</p>
                {pageTypeResult.key_requirements.map((r: any, i: number) => (
                  <div key={i} className="flex items-start gap-2 mb-2 text-sm">
                    <Badge variant={r.importance === 'critical' ? 'default' : 'outline'} className="flex-shrink-0 mt-0.5 text-xs">{r.importance}</Badge>
                    <div><strong>{r.requirement}</strong>{r.why ? `: ${r.why}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
            {pageTypeResult?.recommended_products?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Recommended Products</p>
                {pageTypeResult.recommended_products.map((p: any, i: number) => (
                  <div key={i} className="p-2 border rounded mb-2">
                    <p className="font-medium text-sm">{p.title} <span className="text-muted-foreground">({p.brand})</span></p>
                    <p className="text-xs text-muted-foreground">{p.why_suited}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Persona: top picks + profile */}
            {pageTypeResult?.persona_profile && (
              <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                <p className="text-sm font-medium">{pageTypeResult.persona_profile.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{pageTypeResult.persona_profile.description}</p>
                {pageTypeResult.persona_profile.priorities?.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {pageTypeResult.persona_profile.priorities.map((p: string, i: number) => <Badge key={i} variant="outline" className="text-xs">{p}</Badge>)}
                  </div>
                )}
              </div>
            )}
            {pageTypeResult?.top_picks?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Top Picks</p>
                {pageTypeResult.top_picks.map((pick: any, i: number) => (
                  <div key={i} className="mb-2 p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline">#{pick.rank}</Badge>
                      <span className="font-medium text-sm">{pick.title}</span>
                      <span className="text-xs text-muted-foreground">({pick.brand})</span>
                    </div>
                    <p className="text-xs">{pick.why_perfect}</p>
                  </div>
                ))}
              </div>
            )}

            {/* FAQs */}
            {pageTypeResult?.faqs?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">FAQs</p>
                {pageTypeResult.faqs.map((faq: any, i: number) => (
                  <div key={i} className="mb-2">
                    <p className="text-sm font-medium">{faq.question}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{faq.answer}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Verdict / recommendation */}
            {pageTypeResult?.verdict && (
              <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                <p className="text-sm font-medium mb-1">Verdict</p>
                <p className="text-sm whitespace-pre-wrap">{pageTypeResult.verdict}</p>
              </div>
            )}
            {pageTypeResult?.recommendation && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <p className="text-sm font-medium mb-1">Recommendation</p>
                <p className="text-sm">{pageTypeResult.recommendation}</p>
              </div>
            )}

            {/* Raw JSON toggle (collapsed by default) */}
            <details className="mt-2">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Show raw JSON</summary>
              <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto max-h-60">
                {JSON.stringify(pageTypeResult, null, 2)}
              </pre>
            </details>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category AEO Content Dialog */}
      <Dialog open={showCategoryAEOModal} onOpenChange={setShowCategoryAEOModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-600" />
                  AEO Category Content
                </DialogTitle>
                <DialogDescription>
                  {category.category_name} - {categoryAEOContent?.category_path || category.category_path}
                </DialogDescription>
              </div>
              {categoryAEOContent?.quality && (
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      categoryAEOContent.quality.grade === 'A' ? 'default' :
                      categoryAEOContent.quality.grade === 'B' ? 'secondary' :
                      'outline'
                    }
                    className={
                      categoryAEOContent.quality.grade === 'A' ? 'bg-green-600' :
                      categoryAEOContent.quality.grade === 'B' ? 'bg-blue-600' :
                      categoryAEOContent.quality.grade === 'C' ? 'bg-amber-600 text-white' :
                      'bg-red-600 text-white'
                    }
                  >
                    Grade {categoryAEOContent.quality.grade}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {categoryAEOContent.quality.sections_complete}/{categoryAEOContent.quality.sections_total} sections
                  </span>
                </div>
              )}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-4" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            <div className="space-y-6 py-4">
              {categoryAEOContent?.sections && (
                <>
                  {/* Featured Snippet */}
                  {categoryAEOContent.sections.featured_snippet && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          Featured Snippet
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopySection('Featured Snippet', categoryAEOContent.sections.featured_snippet)}
                        >
                          {copiedSection === 'Featured Snippet' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-950/30 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                        <p className="text-sm">{categoryAEOContent.sections.featured_snippet}</p>
                      </div>
                    </div>
                  )}

                  {/* Category Purpose */}
                  {categoryAEOContent.sections.category_purpose && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold flex items-center gap-2">
                          <Target className="h-4 w-4 text-blue-600" />
                          Category Purpose
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopySection('Category Purpose', categoryAEOContent.sections.category_purpose)}
                        >
                          {copiedSection === 'Category Purpose' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-sm whitespace-pre-wrap">{categoryAEOContent.sections.category_purpose}</p>
                      </div>
                    </div>
                  )}

                  {/* Buyer Guide */}
                  {categoryAEOContent.sections.buyer_guide && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Users className="h-4 w-4 text-green-600" />
                        Buyer Guide
                      </h3>
                      <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg border border-green-200 dark:border-green-800">
                        <p className="font-medium mb-3">{categoryAEOContent.sections.buyer_guide.intro}</p>
                        <div className="space-y-2">
                          {categoryAEOContent.sections.buyer_guide.rules?.map((rule: any, idx: number) => (
                            <div key={idx} className="flex gap-2 text-sm">
                              <span className="text-green-600 font-medium">•</span>
                              <div>
                                <span className="font-medium">{rule.profile}</span>
                                <span className="text-muted-foreground"> → {rule.recommendation}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Differentiators */}
                  {categoryAEOContent.sections.differentiators?.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-amber-600" />
                        Key Differentiators
                      </h3>
                      <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                        <ul className="space-y-2">
                          {categoryAEOContent.sections.differentiators.map((diff: string, idx: number) => (
                            <li key={idx} className="text-sm flex gap-2">
                              <span className="text-amber-600">✦</span>
                              {diff}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* ============ CRITICAL AEO SECTIONS: PRODUCT RECOMMENDATIONS ============ */}

                  {/* Use Cases with Specific Products (Best For...) */}
                  {categoryAEOContent.sections.use_cases?.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Target className="h-4 w-4 text-emerald-600" />
                        Best For... (Use Case Recommendations)
                      </h3>
                      <div className="bg-emerald-50 dark:bg-emerald-950/30 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
                        <div className="grid gap-4">
                          {categoryAEOContent.sections.use_cases.map((useCase: any, idx: number) => (
                            <div key={idx} className="border-b border-emerald-200 dark:border-emerald-700 pb-3 last:border-0 last:pb-0">
                              <div className="flex items-start gap-3">
                                <div className="flex-1">
                                  <p className="font-semibold text-emerald-800 dark:text-emerald-200">{useCase.use_case}</p>
                                  <p className="text-xs text-muted-foreground mb-2">{useCase.description}</p>
                                  {useCase.recommended_product && (
                                    <div className="bg-white dark:bg-emerald-900/50 p-2 rounded flex items-center gap-3">
                                      {useCase.recommended_product.image_url && (
                                        <img
                                          src={useCase.recommended_product.image_url}
                                          alt={useCase.recommended_product.title}
                                          className="w-12 h-12 object-cover rounded"
                                        />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{useCase.recommended_product.title}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {useCase.recommended_product.brand} • {useCase.recommended_product.price}€
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                  {useCase.why_recommended && (
                                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 italic">{useCase.why_recommended}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Top Picks with AI Summaries */}
                  {categoryAEOContent.sections.top_picks?.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-orange-600" />
                        Top Picks (AI-Ready Summaries)
                      </h3>
                      <div className="bg-orange-50 dark:bg-orange-950/30 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                        <div className="grid gap-4">
                          {categoryAEOContent.sections.top_picks.slice(0, 5).map((pick: any, idx: number) => (
                            <div key={idx} className="border-b border-orange-200 dark:border-orange-700 pb-3 last:border-0 last:pb-0">
                              <div className="flex items-start gap-3">
                                {pick.image_url && (
                                  <img
                                    src={pick.image_url}
                                    alt={pick.title}
                                    className="w-16 h-16 object-cover rounded"
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
                                    <p className="text-sm font-medium truncate">{pick.title}</p>
                                  </div>
                                  <p className="text-xs text-muted-foreground mb-1">
                                    {pick.brand} • {pick.price}€
                                  </p>
                                  {pick.ai_summary && (
                                    <p className="text-xs text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/50 p-2 rounded italic">
                                      "{pick.ai_summary}"
                                    </p>
                                  )}
                                  {pick.quick_facts && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {pick.quick_facts.top_features?.slice(0, 3).map((feature: string, fidx: number) => (
                                        <Badge key={fidx} variant="secondary" className="text-[10px]">{feature}</Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Persona-based Recommendations */}
                  {categoryAEOContent.sections.personas?.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Users className="h-4 w-4 text-pink-600" />
                        Recommendations by User Type
                      </h3>
                      <div className="bg-pink-50 dark:bg-pink-950/30 p-4 rounded-lg border border-pink-200 dark:border-pink-800">
                        <div className="grid gap-4">
                          {categoryAEOContent.sections.personas.slice(0, 5).map((persona: any, idx: number) => (
                            <div key={idx} className="border-b border-pink-200 dark:border-pink-700 pb-3 last:border-0 last:pb-0">
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-pink-200 dark:bg-pink-800 flex items-center justify-center text-pink-700 dark:text-pink-200 font-bold text-sm">
                                  {persona.name?.charAt(0) || '?'}
                                </div>
                                <div className="flex-1">
                                  <p className="font-semibold text-pink-800 dark:text-pink-200">{persona.name}</p>
                                  <p className="text-xs text-muted-foreground mb-2">{persona.summary}</p>
                                  {persona.needs?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2">
                                      {persona.needs.slice(0, 3).map((need: string, nidx: number) => (
                                        <Badge key={nidx} variant="outline" className="text-[10px]">{need}</Badge>
                                      ))}
                                    </div>
                                  )}
                                  {persona.recommended_products?.length > 0 && (
                                    <div className="space-y-1">
                                      <p className="text-xs font-medium text-pink-600">Recommended:</p>
                                      {persona.recommended_products.slice(0, 2).map((prod: any, pidx: number) => (
                                        <div key={pidx} className="bg-white dark:bg-pink-900/50 p-2 rounded flex items-center gap-2">
                                          {prod.image_url && (
                                            <img src={prod.image_url} alt={prod.title} className="w-8 h-8 object-cover rounded" />
                                          )}
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium truncate">{prod.title}</p>
                                            <p className="text-[10px] text-muted-foreground">{prod.brand} • {prod.price}€</p>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Price Tiers */}
                  {categoryAEOContent.sections.price_tiers?.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <ShoppingBag className="h-4 w-4 text-yellow-600" />
                        Price Tier Recommendations
                      </h3>
                      <div className="bg-yellow-50 dark:bg-yellow-950/30 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <div className="grid grid-cols-2 gap-3">
                          {categoryAEOContent.sections.price_tiers.map((tier: any, idx: number) => (
                            <div key={idx} className="bg-white dark:bg-yellow-900/50 p-3 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className={
                                  tier.name === 'budget' ? 'bg-green-100 text-green-800' :
                                  tier.name === 'mid_range' ? 'bg-blue-100 text-blue-800' :
                                  tier.name === 'premium' ? 'bg-purple-100 text-purple-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }>
                                  {tier.label || tier.name}
                                </Badge>
                                <span className="text-xs text-muted-foreground">{tier.priceDisplay || `${tier.minPrice}-${tier.maxPrice}€`}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mb-1">{tier.productCount} products</p>
                              {tier.topPick && (
                                <div className="flex items-center gap-2 mt-2">
                                  {tier.topPick.image_url && (
                                    <img src={tier.topPick.image_url} alt={tier.topPick.title} className="w-10 h-10 object-cover rounded" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate">{tier.topPick.title}</p>
                                    <p className="text-[10px] text-muted-foreground">{tier.topPick.price}€</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Subcategory Taxonomy */}
                  {categoryAEOContent.sections.subcategory_taxonomy?.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-slate-600" />
                        Subcategory Taxonomy
                      </h3>
                      <div className="bg-slate-50 dark:bg-slate-950/30 p-4 rounded-lg border border-slate-200 dark:border-slate-800">
                        <div className="flex flex-wrap gap-2">
                          {categoryAEOContent.sections.subcategory_taxonomy.map((sub: any, idx: number) => (
                            <Badge key={idx} variant={sub.suggested ? 'secondary' : 'default'} className="text-xs">
                              {sub.name}
                              {sub.product_count > 0 && <span className="ml-1 opacity-60">({sub.product_count})</span>}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* FAQs */}
                  {categoryAEOContent.sections.faqs?.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold flex items-center gap-2">
                          <FileText className="h-4 w-4 text-indigo-600" />
                          Frequently Asked Questions ({categoryAEOContent.sections.faqs.length})
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopySection('FAQs', categoryAEOContent.sections.faqs.map((f: any) => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n'))}
                        >
                          {copiedSection === 'FAQs' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <div className="space-y-3">
                        {categoryAEOContent.sections.faqs.map((faq: any, idx: number) => (
                          <div key={idx} className="bg-indigo-50 dark:bg-indigo-950/30 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
                            <p className="font-medium text-sm text-indigo-900 dark:text-indigo-100 mb-2">Q: {faq.question}</p>
                            <p className="text-sm text-indigo-700 dark:text-indigo-300">A: {faq.answer}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AEO Questions Management - shown after content is saved */}
                  {categoryAEOMetadata && siteId && (
                    <AEOQuestionsPanel
                      siteId={siteId}
                      contentId={categoryAEOMetadata.id}
                      categoryPath={category.category_path || category.full_path}
                    />
                  )}

                  {/* Knowledge Graph */}
                  {categoryAEOContent.sections.knowledge_graph && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-cyan-600" />
                        Knowledge Graph
                      </h3>
                      <div className="bg-cyan-50 dark:bg-cyan-950/30 p-4 rounded-lg border border-cyan-200 dark:border-cyan-800">
                        {categoryAEOContent.sections.knowledge_graph.parent_category && (
                          <div className="mb-3">
                            <span className="text-xs font-medium text-muted-foreground">Parent Category:</span>
                            <p className="text-sm">{categoryAEOContent.sections.knowledge_graph.parent_category}</p>
                          </div>
                        )}
                        {categoryAEOContent.sections.knowledge_graph.sibling_categories?.length > 0 && (
                          <div className="mb-3">
                            <span className="text-xs font-medium text-muted-foreground">Related Categories:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {categoryAEOContent.sections.knowledge_graph.sibling_categories.map((cat: any, idx: number) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {cat.name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {categoryAEOContent.sections.knowledge_graph.featured_brands?.length > 0 && (
                          <div>
                            <span className="text-xs font-medium text-muted-foreground">Featured Brands:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {categoryAEOContent.sections.knowledge_graph.featured_brands.map((brand: string, idx: number) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {brand}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Narrative */}
                  {categoryAEOContent.sections.narrative && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold flex items-center gap-2">
                          <FileText className="h-4 w-4 text-rose-600" />
                          Category Narrative
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopySection('Narrative', categoryAEOContent.sections.narrative)}
                        >
                          {copiedSection === 'Narrative' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <div className="bg-rose-50 dark:bg-rose-950/30 p-4 rounded-lg border border-rose-200 dark:border-rose-800 max-h-60 overflow-y-auto">
                        <p className="text-sm whitespace-pre-wrap">{categoryAEOContent.sections.narrative}</p>
                      </div>
                    </div>
                  )}

                  {/* AI Context Summary */}
                  {categoryAEOContent.sections.ai_context_summary && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold flex items-center gap-2">
                          <Wand2 className="h-4 w-4 text-violet-600" />
                          AI Context Summary
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopySection('AI Context', JSON.stringify(categoryAEOContent.sections.ai_context_summary, null, 2))}
                        >
                          {copiedSection === 'AI Context' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <div className="bg-violet-50 dark:bg-violet-950/30 p-4 rounded-lg border border-violet-200 dark:border-violet-800">
                        <pre className="text-xs overflow-x-auto">
                          {JSON.stringify(categoryAEOContent.sections.ai_context_summary, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* CTA Alternatives */}
                  {categoryAEOContent.sections.cta_alternatives && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <ExternalLink className="h-4 w-4 text-teal-600" />
                        Call-to-Action Options
                      </h3>
                      <div className="bg-teal-50 dark:bg-teal-950/30 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
                        {categoryAEOContent.sections.cta_alternatives.primary_cta && (
                          <div className="mb-3">
                            <span className="text-xs font-medium text-muted-foreground">Primary CTA:</span>
                            <p className="text-sm font-medium">{categoryAEOContent.sections.cta_alternatives.primary_cta.text}</p>
                          </div>
                        )}
                        {categoryAEOContent.sections.cta_alternatives.alternatives?.length > 0 && (
                          <div className="mb-3">
                            <span className="text-xs font-medium text-muted-foreground">Alternative CTAs:</span>
                            <div className="space-y-1 mt-1">
                              {categoryAEOContent.sections.cta_alternatives.alternatives.map((alt: any, idx: number) => (
                                <p key={idx} className="text-sm">• {alt.text}</p>
                              ))}
                            </div>
                          </div>
                        )}
                        {categoryAEOContent.sections.cta_alternatives.parent_category && (
                          <div>
                            <span className="text-xs font-medium text-muted-foreground">Back Link:</span>
                            <p className="text-sm">{categoryAEOContent.sections.cta_alternatives.parent_category.text}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4 border-t">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {categoryAEOContent?.from_cache ? (
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    From cache
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Sparkles className="h-4 w-4" />
                    Freshly generated
                  </span>
                )}
                {categoryAEOMetadata && (
                  <Badge variant="outline" className="ml-2">
                    Saved v{categoryAEOMetadata.version || 1}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                {!categoryAEOMetadata && (
                  <Button
                    onClick={handleSaveCategoryAEO}
                    disabled={savingCategoryAEO || !categoryAEOContent}
                  >
                    {savingCategoryAEO ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => handleGenerateCategoryAEO(true)}
                  disabled={generatingCategoryAEO}
                >
                  {generatingCategoryAEO ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Regenerate
                </Button>
                <Button variant="secondary" onClick={() => setShowCategoryAEOModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
